// HRRR-Smoke surface PM2.5 raster layer.
//
// Displays NOAA HRRR-Smoke MASSDEN concentration as a translucent
// colored overlay over the HRRR domain (lon −130 to −60, lat 20 to 72).
// Phase 1: surface only (8 m AGL). Phase 2: forecast time scrubber
// across published hours. Phase 3: altitude-band selection — the
// existing AltitudeSlider (right rail) drives which sigma-level PNG
// is shown.
//
// Architecture:
//   - Image source (NOT a tile pyramid). The ingest publishes one
//     global PNG per (forecast hour, hybrid level) per cycle.
//   - applyState() re-fetches the image when smokeOffsetH OR
//     altitudeSliderFt changes (the visibility useEffect in MapView
//     lists both as deps).
//   - pickClosestLevel() snaps the slider's MSL altitude to the
//     closest published hybrid level via the LEVEL_ALT_AGL_FT table
//     (kept in sync with smoke_ingest.py). MSL ≈ AGL for the slider's
//     pilot use case (terrain elev is small compared to flight alt).

import type { Map as MlMap } from 'maplibre-gl';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';

// HRRR-Smoke domain bounds (matches the Python ingest crop).
const HRRR_DOMAIN_COORDS: [number, number][] = [
  [-130, 72], // top-left
  [ -60, 72], // top-right
  [ -60, 20], // bottom-right
  [-130, 20], // bottom-left
];

// Phase 1 published forecast hours. Keep in sync with FORECAST_HOURS in
// scripts/smoke-ingest/smoke_ingest.py and the SMOKE_HOURS in SmokeBar.tsx.
const PHASE_1_HOURS = [0, 3, 6, 12, 18] as const;

// Phase 3 published hybrid levels + approximate AGL altitudes (ft). Keep
// in sync with LEVEL_ALT_AGL_FT in scripts/smoke-ingest/smoke_ingest.py.
const SMOKE_LEVEL_ALT_FT: Record<number, number> = {
  0:     30,
  1:    100,
  5:   1000,
  10:  3300,
  15:  6200,
  20:  9500,
  25: 13800,
};
const SMOKE_LEVELS = Object.keys(SMOKE_LEVEL_ALT_FT)
  .map((k) => parseInt(k, 10))
  .sort((a, b) => a - b);

/** Snap an arbitrary forecast offset to the closest published hour. */
function pickForecastHour(offsetH: number): number {
  const target = Math.max(0, Math.round(offsetH));
  let best = PHASE_1_HOURS[0] as number;
  let bestDist = Math.abs(target - best);
  for (const h of PHASE_1_HOURS) {
    const d = Math.abs(target - h);
    if (d < bestDist) {
      best = h;
      bestDist = d;
    }
  }
  return best;
}

/** Snap an MSL altitude (ft) to the closest published hybrid level. */
export function pickClosestLevel(altFt: number): number {
  const target = Math.max(0, altFt);
  let best = SMOKE_LEVELS[0];
  let bestDist = Math.abs(target - SMOKE_LEVEL_ALT_FT[best]);
  for (const lv of SMOKE_LEVELS) {
    const d = Math.abs(target - SMOKE_LEVEL_ALT_FT[lv]);
    if (d < bestDist) {
      best = lv;
      bestDist = d;
    }
  }
  return best;
}

function smokeImageUrl(offsetH: number, altFt: number): string {
  const hour = pickForecastHour(offsetH);
  const level = pickClosestLevel(altFt);
  return `/api/wx/smoke?hour=${hour}&level=${level}`;
}

/**
 * Mount the HRRR-Smoke image source + raster layer. Idempotent.
 */
export function addHrrrSmokeLayers(map: MlMap): void {
  if (!map.getSource('hrrr-smoke')) {
    map.addSource('hrrr-smoke', {
      type: 'image',
      url: smokeImageUrl(0, 0),
      coordinates: HRRR_DOMAIN_COORDS,
    } as any);

    const beforeId = (() => {
      // Sit beneath the airspace polygons + sfc analysis but above NEXRAD.
      for (const id of ['sfcanalysis-raster', 'sua-fill', 'tfr-fill', 'fires-fill']) {
        if (map.getLayer(id)) return id;
      }
      return undefined;
    })();

    map.addLayer(
      {
        id: 'hrrr-smoke-raster',
        type: 'raster',
        source: 'hrrr-smoke',
        layout: { visibility: 'none' },
        paint: {
          // 0.65 keeps red/brown plumes clearly visible while letting
          // basemap + chart text show through.
          'raster-opacity': 0.65,
        },
      },
      beforeId,
    );
  }
}

registerLayerFamily({
  id: 'hrrr-smoke',
  label: 'HRRR Smoke',
  sourceId: 'hrrr-smoke',
  layerIds: ['hrrr-smoke-raster'],
  mount: (map) => addHrrrSmokeLayers(map),
  visibilitySelector: (l) => l['hrrr-smoke'],
  applyState: (map) => {
    if (!map.getLayer('hrrr-smoke-raster')) return;
    const show = useLayerVisibilityStore.getState().layers['hrrr-smoke'];
    map.setLayoutProperty('hrrr-smoke-raster', 'visibility', show ? 'visible' : 'none');
    if (show) {
      // Re-fetch image when forecast scrubber OR altitude slider moves.
      const { smokeOffsetH, altitudeSliderFt } = useUiStore.getState();
      const src = map.getSource('hrrr-smoke') as
        | { updateImage?: (opts: { url: string }) => void }
        | undefined;
      src?.updateImage?.({ url: smokeImageUrl(smokeOffsetH, altitudeSliderFt) });
    }
  },
});
